import React, { useEffect, useState } from "react";
import { Card, Collapse, Button, message, Tooltip } from "antd";
import { useMsal } from "@azure/msal-react";
import { callGetSession } from "components/LaunchPositWorkbenchModal/launchPositWorkbenchApi";
import { LinkCard } from "./types";
import { linksData } from "./constants";
import { ISusData } from "components/SpaceList";
import { getSagemakerDomainByDisplayName } from "utils/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import { GRAFANA_WORKSPACE_BASE_URL, GRAFANA_LOG_GROUP_CONFIG } from "const";
import "./style.scss";
import LaunchPositWorkbenchModal from "components/LaunchPositWorkbenchModal";
 
const LinksSection: React.FC<{ 
  spaceDisplayName: string, 
  sageMakerData: ISusData, 
  domainName: string,
  region?: string,
  bucketName?: string,
  dataAccountId?: string,
  gxpFlag?: string,
  profileType?: string
}> = ({ spaceDisplayName = "Links", sageMakerData, domainName, region, bucketName, dataAccountId, gxpFlag, profileType }) => {
  const [showWorkbenchModal, setShowWorkbenchModal] = useState(false);
  const [activeSessionUrl, setActiveSessionUrl] = useState<string | null>(null);
  const msalInstance = useMsal();
  const sagemakerDomain = getSagemakerDomainByDisplayName(domainName) ?? "";

  // When the displayed space changes, check for an active Posit session for that space
  useEffect(() => {
    let cancelled = false;
    const checkSession = async () => {
      setActiveSessionUrl(null);
      if (!spaceDisplayName) return;
      try {
        const result = await callGetSession(msalInstance, spaceDisplayName);
        if (!cancelled && result?.hasActiveSession && result.url) {
          setActiveSessionUrl(result.url);
        }
      } catch (err) {
        if (!cancelled) setActiveSessionUrl(null);
      }
    };

    checkSession();

    return () => {
      cancelled = true;
    };
  }, [spaceDisplayName, msalInstance]);

  // Guard: if linksData is null or undefined, render nothing or a fallback
  if (!linksData || !Array.isArray(linksData) || linksData.length === 0) {
    return <div>No links available.</div>;
  }

  // Dynamically update linksData with sagemakerData
  const dynamicLinksData = linksData.map(link => {
    if (link.linkFor === "sagemaker_studio" && sageMakerData?.projectLink) {
      return { ...link, url: sageMakerData.projectLink };
    }
    if (link.linkFor === "github_project" && sageMakerData?.gitRepo) {
      return { ...link, url: sageMakerData.gitRepo };
    }
    if (link.linkFor === "business_metadata" && sageMakerData?.susProjectMetadata) {
      return { ...link, url: sageMakerData.susProjectMetadata };
    }
    if (link.linkFor === "grafana_logs" && region && dataAccountId) {
      const base = new URL(`${GRAFANA_WORKSPACE_BASE_URL}/d/lYQ8-oQGf/application-logs`);
      base.searchParams.set("orgId", "1");
      base.searchParams.set("var-Region", region);
      base.searchParams.set("var-Accounts", dataAccountId);
      base.searchParams.set("var-search", "");
      const logGroups = GRAFANA_LOG_GROUP_CONFIG[profileType ?? ""] ?? ["$__all"];
      logGroups.forEach(lg => {
        const logGroupArn = lg === "$__all" 
          ? lg 
          : `arn:aws:logs:${region}:${dataAccountId}:log-group:${lg}:*`;
        base.searchParams.append("var-log_group_name", logGroupArn);
      });
      base.searchParams.set("from", "now-1h");
      base.searchParams.set("to", "now");
      return { ...link, url: base.toString() };
    }

    return link;
  });

  const handleButtonClick = (link: LinkCard) => {
    // Special handling for Post Workbench - open modal on click
    if (link.linkFor === "post_workbench") {
      // If an active session exists, open it directly
      if (activeSessionUrl) {
        try {
          window.open(activeSessionUrl, "_blank", "noopener,noreferrer");
        } catch (err) {}
        return;
      }
      setShowWorkbenchModal(true);
      return;
    }
    
    window.open(link.url, "_blank", "noopener,noreferrer");
  };
 
  return (
    <div style={{ backgroundColor: "#f5f5f5" }}>
      <Collapse defaultActiveKey={["1"]} style={{ backgroundColor: "#f5f5f6" }}>
        <Collapse.Panel
          key="1"
          header={
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>
                Links inside <strong>{spaceDisplayName}</strong>
              </span>
            </div>
          }
        >
          <div style={{ overflowX: "auto", paddingBottom: "16px" }}>
            <div style={{ display: "flex", gap: "16px", minWidth: "fit-content" }}>
              {dynamicLinksData.map((link) => (
                <Card
                  key={link.title}
                  style={{
                    flex: "0 0 auto",
                    width: "23.9%",
                    minHeight: link.linkFor === "post_workbench" ? "160px" : "110px",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    transition: "box-shadow 0.3s",
                  }}
                  hoverable
                  bodyStyle={{ padding: "12px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", textAlign: "center", height: "100%" }}
                >
                  <p style={{ margin: 0, fontSize: "14px", color: "#000000", flex: 1, padding: "0 8px" }}>
                    {link.description}
                  </p>
                  
                  <Tooltip
                    title={
                      (!link.url || link.url === "#") && (link.linkFor === "sagemaker_studio" || link.linkFor === "github_project" || link.linkFor === "business_metadata")
                        ? "Link not available. Please refresh the the page or space list"
                        : ""
                    }
                    overlayStyle={{
                      maxWidth: 220,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >                    
                    <Button
                      type="primary"
                      onClick={() => handleButtonClick(link)}
                      style={{
                        marginTop: "12px",
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#fff",
                        backgroundColor: "#1890ff",
                        borderColor: "#1890ff",
                        width: "100%",
                        height: "32px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      disabled={
                        link.disable || 
                        (link.linkFor !== "post_workbench" && (!link.url || link.url === "#"))
                      }
                      icon={
                        (!link.url || link.url === "#") &&
                          (link.linkFor === "sagemaker_studio" || link.linkFor === "github_project" || link.linkFor === "business_metadata") ? (
                          <InfoCircleOutlined />
                        ) : null
                      }
                    >
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: "80%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          verticalAlign: "middle",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {link.linkFor === "post_workbench" && activeSessionUrl ? "Open Active Session" : link.buttonText}
                      </span>
                    </Button>
                  </Tooltip>
                </Card>
              ))}
            </div>
          </div>
        </Collapse.Panel>
      </Collapse>
      {/* Modal for launching Posit Workbench - fetches data on open */}
      <LaunchPositWorkbenchModal
        visible={showWorkbenchModal}
        onCancel={() => setShowWorkbenchModal(false)}
        msalInstance={msalInstance}
        region={region}
        bucketName={bucketName}
        dataAccountId={dataAccountId}
        spaceDisplayName={spaceDisplayName}
        gxpFlag={gxpFlag}
        onLaunched={(url) => {
          setActiveSessionUrl(url);
          setShowWorkbenchModal(false);
        }}
      />
    </div>
  );
};
 
export default LinksSection;